import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CatalogFilters } from "../components/catalog-filters";
import { ProductCard } from "../components/product-card";
import { useCart } from "../hooks/use-cart";
import { categoryPath, fetchCatalog, productPath, type CatalogProduct, type CatalogResponse } from "../lib/catalog";
import { formatCurrency, toSentenceCase } from "../lib/utils";

export function ShopPage() {
  const routeParams = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState<CatalogResponse | null>(null);
  const [favoriteItems, setFavoriteItems] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favoritesLoading, setFavoritesLoading] = useState(true);
  const [activeFavoriteIndex, setActiveFavoriteIndex] = useState(0);
  const { addOne, cartCounts } = useCart();

  useEffect(() => {
    let ignore = false;

    async function loadCatalog() {
      setLoading(true);
      setError(null);

      try {
        const requestParams = new URLSearchParams(params);
        const categoryId = routeParams.categoryId;
        const categoryName = routeParams.categoryName;
        if (categoryId && !requestParams.get("category_id")) {
          requestParams.set("category_id", categoryId);
        }
        if (categoryName && !requestParams.get("category") && !requestParams.get("category_id")) {
          requestParams.set("category", decodeURIComponent(categoryName));
        }

        const result = await fetchCatalog(requestParams);
        if (!ignore) {
          setData(result);
        }
      } catch {
        if (!ignore) {
          setError("Unable to load the catalog right now.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void loadCatalog();
    return () => {
      ignore = true;
    };
  }, [params, routeParams.categoryName]);

  useEffect(() => {
    let ignore = false;

    async function loadFavoriteItems() {
      setFavoritesLoading(true);

      try {
        const requestParams = new URLSearchParams();
        requestParams.set("sort", "popular");
        requestParams.set("limit", "10");
        const result = await fetchCatalog(requestParams);
        if (!ignore) {
          setFavoriteItems(result.products);
        }
      } catch {
        if (!ignore) {
          setFavoriteItems([]);
        }
      } finally {
        if (!ignore) {
          setFavoritesLoading(false);
        }
      }
    }

    void loadFavoriteItems();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    setActiveFavoriteIndex(0);
  }, [favoriteItems.length]);

  useEffect(() => {
    if (favoriteItems.length < 2) {
      return;
    }

    const timer = window.setInterval(() => {
      setActiveFavoriteIndex((current) => (current + 1) % favoriteItems.length);
    }, 4200);

    return () => {
      window.clearInterval(timer);
    };
  }, [favoriteItems]);

  const featuredCategories = data?.categories.filter((item) => item.productCount > 0).slice(0, 6) || [];
  const activeFavorite = favoriteItems[activeFavoriteIndex] ?? null;
  const favoriteThumbs = useMemo(() => favoriteItems.slice(0, 5), [favoriteItems]);

  return (
    <section className="mx-auto w-full max-w-[96rem] px-2 py-8 sm:px-4 lg:px-5 lg:py-12">
      <div className="relative overflow-hidden rounded-[36px] border border-black/8 bg-[linear-gradient(135deg,#071526,#0f2f56_54%,#0ea5b7)] text-white shadow-[0_28px_80px_rgba(8,23,44,0.18)]">
        {activeFavorite?.image ? (
          <img alt={toSentenceCase(activeFavorite.name)} className="absolute inset-0 h-full w-full object-cover opacity-32" src={activeFavorite.image} />
        ) : null}
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.92),rgba(2,6,23,0.65),rgba(2,6,23,0.84)),radial-gradient(circle_at_top_left,rgba(255,209,102,0.22),transparent_28%),radial-gradient(circle_at_80%_18%,rgba(255,255,255,0.14),transparent_24%)]" />
        <div className="relative grid gap-8 px-5 py-6 sm:px-7 lg:px-8 lg:py-8 xl:grid-cols-[minmax(0,1.15fr)_380px] xl:items-end">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-cyan-200">Favourite items</p>
            <h1 className="mt-3 max-w-4xl text-4xl font-semibold tracking-tight sm:text-5xl">
              Trending favorites from the Novatech catalog.
            </h1>
            {favoritesLoading ? (
              <div className="mt-6 text-sm text-slate-200/85">Loading favourite items...</div>
            ) : favoriteItems.length > 0 ? (
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur-md">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">Top item</p>
                  <p className="mt-2 text-lg font-semibold">{toSentenceCase(activeFavorite?.name || "")}</p>
                </div>
                <div className="rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur-md">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">Price</p>
                  <p className="mt-2 text-lg font-semibold">{activeFavorite ? formatCurrency(activeFavorite.price) : "..."}</p>
                </div>
                <div className="rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur-md">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">Stock</p>
                  <p className="mt-2 text-lg font-semibold">{activeFavorite?.inStock ? "Available now" : "Out of stock"}</p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="rounded-[32px] border border-white/12 bg-white/10 p-4 backdrop-blur-md">
            {activeFavorite ? (
              <>
                <Link className="block overflow-hidden rounded-[26px] bg-white/8" to={productPath(activeFavorite.slug, activeFavorite.id)}>
                  <div className="aspect-[4/3] overflow-hidden">
                    {activeFavorite.image ? (
                      <img
                        alt={toSentenceCase(activeFavorite.name)}
                        className="h-full w-full object-cover transition duration-300 hover:scale-[1.03]"
                        src={activeFavorite.image}
                      />
                    ) : (
                      <div className="grid h-full place-items-center text-sm text-slate-200/80">No image available</div>
                    )}
                  </div>
                </Link>
                <div className="mt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-200">
                    Favourite item #{activeFavoriteIndex + 1}
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight">{toSentenceCase(activeFavorite.name)}</h2>
                  <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-200/85">
                    {activeFavorite.description || "Open the product to view the full specs, stock context, and related items."}
                  </p>
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.16em] text-slate-300">Category</p>
                      <p className="mt-1 text-sm font-semibold text-white">{activeFavorite.category}</p>
                    </div>
                    <Link
                      className="inline-flex items-center rounded-full border border-white/14 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/16"
                      to={productPath(activeFavorite.slug, activeFavorite.id)}
                    >
                      View item
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <div className="grid min-h-[320px] place-items-center rounded-[26px] bg-white/8 p-6 text-center text-sm text-slate-200/85">
                No favourite items are available yet.
              </div>
            )}

            {favoriteThumbs.length > 1 ? (
              <div className="mt-4 grid grid-cols-5 gap-3">
                {favoriteThumbs.map((item, index) => (
                  <button
                    className={`overflow-hidden rounded-[18px] border transition ${
                      favoriteItems[activeFavoriteIndex]?.id === item.id
                        ? "border-cyan-300 shadow-[0_0_0_1px_rgba(103,232,249,0.35)]"
                        : "border-white/10 opacity-75 hover:opacity-100"
                    }`}
                    key={item.id}
                    onClick={() => setActiveFavoriteIndex(index)}
                    type="button"
                  >
                    {item.image ? (
                      <img alt={toSentenceCase(item.name)} className="aspect-square w-full object-cover" src={item.image} />
                    ) : (
                      <div className="grid aspect-square place-items-center bg-white/8 text-[10px] text-slate-200/80">Item</div>
                    )}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="mt-8 grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-6">
        <aside className="glass-panel rounded-[30px] p-4 sm:p-5 lg:sticky lg:top-24 lg:self-start">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="eyebrow">Filter the catalog</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">Refine the parts wall</h2>
            </div>
            <div className="rounded-2xl border border-black/8 bg-white/70 px-3 py-2 text-right text-xs font-semibold uppercase tracking-[0.18em] text-nv-slate dark:border-white/10 dark:bg-white/6">
              {data ? `${data.meta.totalProducts} shown` : "Loading"}
            </div>
          </div>
          {data ? (
            <div className="mt-4 sm:mt-5">
              <CatalogFilters
                basePath="/shop"
                categories={data.categories}
                initialQuery={params.get("q") || ""}
                initialCategory={params.get("category") || ""}
                initialInStock={params.get("in_stock") === "true"}
                initialMax={params.get("max") || ""}
                initialMin={params.get("min") || ""}
                initialSort={params.get("sort") || "featured"}
              />
            </div>
          ) : null}
        </aside>
        <div className="min-w-0">
          {loading ? (
            <div className="glass-panel rounded-[30px] p-8 text-sm text-nv-slate">Loading products...</div>
          ) : error ? (
            <div className="glass-panel rounded-[30px] p-8 text-sm font-medium text-red-600">{error}</div>
          ) : data && data.products.length > 0 ? (
            <div className="space-y-4 sm:space-y-5">
              <div
                className="scroll-mt-28 flex flex-wrap items-center justify-between gap-3 rounded-[28px] border border-black/8 bg-white/70 px-5 py-4 text-sm shadow-[0_18px_45px_rgba(16,32,51,0.06)] backdrop-blur-md dark:border-white/10 dark:bg-white/6"
                id="shop-categories"
              >
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Catalog view</p>
                  <p className="mt-1 font-semibold text-nv-ink">
                    {data.meta.totalProducts} products across {featuredCategories.length || data.categories.filter((item) => item.productCount > 0).length} active categories
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {featuredCategories.slice(0, 3).map((item) => (
                    <span
                      className="rounded-full border border-black/8 bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-nv-slate dark:border-white/10 dark:bg-white/8"
                      key={item.id}
                    >
                      {item.name}
                    </span>
                  ))}
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2 md:gap-5 2xl:grid-cols-3">
                {data.products.map((item) => (
                  <ProductCard
                    cartQuantity={cartCounts.get(item.id) || 0}
                    category={item.category}
                    description={item.description}
                    image={item.image}
                    id={item.id}
                    key={item.id}
                    name={item.name}
                    price={item.price}
                    quantity={item.quantity}
                    slug={item.slug}
                    testedWith={item.testedWith}
                    onAddToCart={addOne}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="glass-panel rounded-[30px] p-8">
              <h2 className="text-2xl font-semibold tracking-tight">No products matched those filters</h2>
              <p className="mt-3 text-sm text-nv-slate">
                Try removing price limits or switching back to all categories.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
